import type { EventFilter } from "@/Modules/Chat/WebSocketManager";
import { VIDEO_CALL_COMPLETE_REASON } from "@/Modules/Models/Api/VideoCalls";
import {
  MessageTypeWebSocket,
  type VideoCallRoomCompletedMessageWebSocket,
} from "@/Modules/Chat/Models/WebSocket";

import { VIDEO_CALL_STATUS } from "@/Modules/VideoCall/Components/definitions";

import { type VideoCallStore, useVideoCallStore } from "@/Modules/VideoCall/Store";
import { type UserStore, useUserStore } from "@/Modules/User/Store";

import { hasValue, isObject } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";
import { log } from "@/Utils/Debug";
import { waitFor } from "@/Utils/Promises";
import { MILLISECONDS_TO_SECONDS } from "@/Utils/Date";

const MIN_VIDEO_CALL_DURATION = 2;

export interface ExtraParamsCreateVideoCall {
  videoCallStore: VideoCallStore;
  userStore: UserStore;
}

export const isVideoCallCompletedMessage = (
  message: unknown
): message is VideoCallRoomCompletedMessageWebSocket => {
  return (
    isObject(message) &&
    message.type === MessageTypeWebSocket.VIDEO_CALL_ROOM_COMPLETED &&
    isObject(message.data) &&
    isValidString(message.data.room_uuid)
  );
};

export const videoCallCompletedHandler = async (
  createVideoCallMessageWebSocket: VideoCallRoomCompletedMessageWebSocket,
  { videoCallStore = useVideoCallStore(), userStore = useUserStore() }: ExtraParamsCreateVideoCall
): Promise<undefined> => {
  log("RECEIVE COMPLETE VIDEO EVENT");

  if (
    hasValue(createVideoCallMessageWebSocket.data) &&
    isValidString(createVideoCallMessageWebSocket.data.room_uuid) &&
    videoCallStore.roomId === createVideoCallMessageWebSocket.data.room_uuid
  ) {
    log(`Close open videocall with id ${createVideoCallMessageWebSocket.data.room_uuid}`);

    if (videoCallStore.roomId === createVideoCallMessageWebSocket.data.room_uuid) {
      if (hasValue(videoCallStore.timeCounter)) {
        const { startDate, currentDate } = videoCallStore.timeCounter;

        if (startDate === undefined || currentDate === undefined) {
          await waitFor(0.5);
        } else {
          const diff = currentDate.getTime() - startDate.getTime();
          const seconds = Math.floor(diff / MILLISECONDS_TO_SECONDS);

          if (seconds < MIN_VIDEO_CALL_DURATION) {
            await waitFor(MIN_VIDEO_CALL_DURATION - seconds);
          }
        }
      }

      videoCallStore.updateShowConfig(false);
      videoCallStore.updateNextCallOptions(undefined);

      if (
        userStore.getUserIsTherapist &&
        createVideoCallMessageWebSocket.data.completed_reason === VIDEO_CALL_COMPLETE_REASON.PERMISSIONS
      ) {
        videoCallStore.patientPermissionError = true;
      }

      if (videoCallStore.status !== VIDEO_CALL_STATUS.CLOSING) {
        videoCallStore.updateStatus(VIDEO_CALL_STATUS.UNPUBLISHING);
      }
    }
  } else if (videoCallStore.showConfig) {
    videoCallStore.updateNextCallOptions(undefined);
    videoCallStore.updateShowConfig(false);
  }

  return undefined;
};

export default {
  isType: isVideoCallCompletedMessage,
  handler: videoCallCompletedHandler,
} as EventFilter<ExtraParamsCreateVideoCall>;
